import { AuthError } from "@/modules/auth/authErrorTypes"
import { performAuthLogout } from "@/modules/auth/hooks/useLogout"
import { useAppStateStore } from "@/modules/auth/store/useAppStateStore"
import { getSessionInformations } from "@/modules/auth/utils/getSessionInformations"
import { LoggerType } from "@/modules/utils/loggerUtils"
import { AuthError as AmplifyAuthError } from "aws-amplify/auth"

/**
 * Boot-time guard against a desynced auth state.
 *
 * The app's "logged-in" flag (@auth/user, persisted by Zustand) is independent of Amplify's Cognito
 * token storage. When the Cognito refresh token expires/is revoked, Amplify purges the cached
 * id/access/refresh tokens, but @auth/user stays persisted. The app then boots "authenticated",
 * mounts protected routes, fires API calls that 401, and crashes to a white screen.
 *
 * This runs once on boot (after Amplify.configure): if we believe we're authenticated but Cognito
 * has no usable token, clear the stale app state so the user lands on login instead of a white screen.
 *
 * Transient failures (offline, Pre-Token Lambda errors) must NOT log the user out — they are not a
 * dead session. Only a definitively invalid session (no token, or NotAuthorizedException) clears state.
 */
export const reconcileSession = async () => {
  if (!useAppStateStore.getState().isAuthenticated) return

  try {
    const { idToken, isIdTokenValid } = await getSessionInformations()
    // fetchAuthSession auto-refreshes an expired id token when the refresh token is still valid,
    // so a valid token here means the session is healthy.
    if (idToken && isIdTokenValid) return

    // No usable token and no exception — the session is genuinely gone.
    console.warn(LoggerType.Auth + "[Cognito] reconcileSession: persisted auth but no Cognito token — clearing")
    await performAuthLogout()
  } catch (e) {
    // Offline or transient infra error — keep the session and let the app retry normally.
    if (
      e instanceof AmplifyAuthError &&
      (e.name === AuthError.NetworkError || e.name === AuthError.UserLambdaValidationException)
    ) {
      return
    }

    // NotAuthorizedException (refresh token expired/revoked) or any other auth error — session is dead.
    console.warn(LoggerType.Auth + "[Cognito] reconcileSession: session invalid — clearing", e)
    await performAuthLogout()
  }
}
